import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { toast } from 'sonner';
import { Users, Plus, ArrowRight, Calendar, CheckCircle, Clock, XCircle, Building2, Home } from 'lucide-react';
import { formatDate } from '../lib/utils';

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-50 text-amber-700', icon: Clock, label: 'Pending' },
  contacted: { color: 'bg-blue-50 text-blue-700', icon: ArrowRight, label: 'Contacted' },
  active: { color: 'bg-sky-50 text-sky-700', icon: CheckCircle, label: 'Active' },
  accepted: { color: 'bg-sky-50 text-sky-700', icon: CheckCircle, label: 'Accepted' },
  completed: { color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle, label: 'Completed' },
  declined: { color: 'bg-red-50 text-red-700', icon: XCircle, label: 'Declined' },
};

const REFERRING_TO_CONFIG = {
  purpose_driven: { label: 'Purpose Driven', color: 'bg-teal-50 text-teal-700', icon: Building2 },
  our_square: { label: 'Our Square', color: 'bg-cyan-50 text-cyan-700', icon: Home },
  both: { label: 'Both Programs', color: 'bg-purple-50 text-purple-700', icon: null },
};

export default function ReferralsPage() {
  const { user, axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const [referrals, setReferrals] = useState([]);
  const [clients, setClients] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [filter, setFilter] = useState(statusParam || 'all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ participant_id: '', opportunity_id: '', notes: '', referring_to: 'purpose_driven' });
  const api = axiosAuth();
  
  const canCreate = ['peer_specialist', 'coordinator', 'admin', 'referral_source', 'housing_coordinator'].includes(user.role);
  const canUpdateStatus = ['peer_specialist', 'coordinator', 'admin', 'housing_coordinator'].includes(user.role);
  const isPartner = user.role === 'resource_partner';

  const load = async () => {
    try {
      const [refsRes, clientsRes, oppsRes] = await Promise.all([
        api.get('/referrals'),
        canCreate ? api.get('/users?role=client').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get('/opportunities').catch(() => ({ data: [] }))
      ]);
      setReferrals(refsRes.data || []);
      setClients(clientsRes.data || []);
      setOpportunities(oppsRes.data || []);
    } catch (e) {
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (statusParam) {
      setFilter(statusParam);
    }
  }, [statusParam]);

  const filtered = filter === 'all' 
    ? referrals 
    : referrals.filter(r => {
        if (filter === 'active') {
          return ['active', 'accepted'].includes(r.status);
        }
        return r.status === filter;
      });

  const handleCreate = async () => {
    if (!form.participant_id) {
      toast.error('Please select a participant');
      return;
    }
    try {
      await api.post('/referrals', form);
      toast.success('Referral created!');
      setOpen(false);
      setForm({ participant_id: '', opportunity_id: '', notes: '', referring_to: 'purpose_driven' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create referral');
    }
  };

  const handleStatusChange = async (refId, status) => {
    try {
      await api.put(`/referrals/${refId}/status?status=${status}`);
      toast.success('Status updated');
      load();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
            {isPartner ? 'Referrals Received' : 'Referrals'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isPartner ? 'Participants referred to your opportunities' : 'Track participant referrals to resources and opportunities'}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 rounded-full" data-testid="referrals-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-referral-button">
                  <Plus size={16} /> New Referral
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader>
                  <DialogTitle>Create Referral</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-1.5">
                    <Label>Participant *</Label>
                    <Select value={form.participant_id} onValueChange={v => setForm({...form, participant_id: v})}>
                      <SelectTrigger data-testid="referral-participant-select">
                        <SelectValue placeholder="Select a participant" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Refer To Selection */}
                  <div className="space-y-2">
                    <Label>Refer To *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({...form, referring_to: 'purpose_driven'})}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'purpose_driven' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'}`}
                        data-testid="ref-to-purpose-driven"
                      >
                        <Building2 size={18} className={`mx-auto mb-1 ${form.referring_to === 'purpose_driven' ? 'text-teal-600' : 'text-slate-400'}`} />
                        <p className={`text-xs font-medium ${form.referring_to === 'purpose_driven' ? 'text-teal-700' : 'text-slate-600'}`}>Purpose Driven</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({...form, referring_to: 'our_square'})}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'our_square' ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:border-cyan-300'}`}
                        data-testid="ref-to-our-square"
                      >
                        <Home size={18} className={`mx-auto mb-1 ${form.referring_to === 'our_square' ? 'text-cyan-600' : 'text-slate-400'}`} />
                        <p className={`text-xs font-medium ${form.referring_to === 'our_square' ? 'text-cyan-700' : 'text-slate-600'}`}>Our Square</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({...form, referring_to: 'both'})}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'both' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-300'}`}
                        data-testid="ref-to-both"
                      >
                        <div className="flex justify-center gap-0.5 mb-1">
                          <Building2 size={12} className={form.referring_to === 'both' ? 'text-purple-600' : 'text-slate-400'} />
                          <Home size={12} className={form.referring_to === 'both' ? 'text-purple-600' : 'text-slate-400'} />
                        </div>
                        <p className={`text-xs font-medium ${form.referring_to === 'both' ? 'text-purple-700' : 'text-slate-600'}`}>Both</p>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label>Opportunity (optional)</Label>
                    <Select value={form.opportunity_id} onValueChange={v => setForm({...form, opportunity_id: v === 'none' ? '' : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an opportunity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {opportunities.map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Notes</Label>
                    <DictationInput 
                      value={form.notes}
                      onChange={(val) => setForm({...form, notes: val})}
                      placeholder="Any additional notes about this referral..."
                      rows={3}
                    showCleanup={false} />
                  </div>
                  <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                    Create Referral
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {user.role === 'referral_source' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="referrals-readonly-banner">
          Submitted referrals are read-only once created.
        </div>
      )}

      {/* Stats for Resource Partners */}
      {isPartner && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const count = referrals.filter(r => r.status === status).length;
            const Icon = config.icon;
            return (
              <Card key={status} className="border-0 shadow-sm rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} className={config.color.split(' ')[1]} />
                    <span className="text-xs text-muted-foreground">{config.label}</span>
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Referrals List */}
      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(ref => {
            const statusConfig = STATUS_CONFIG[ref.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            const opp = opportunities.find(o => o.id === ref.opportunity_id);
            const refToConfig = REFERRING_TO_CONFIG[ref.referring_to] || null;
            return (
              <Card key={ref.id} className="border-0 shadow-sm rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={statusConfig.color}>
                            <StatusIcon size={12} className="mr-1" /> {statusConfig.label}
                          </Badge>
                          {refToConfig && (
                            <Badge className={refToConfig.color} variant="outline">
                              {refToConfig.icon && <refToConfig.icon size={10} className="mr-1" />}
                              {refToConfig.label}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(ref.created_at)}
                          </span>
                        </div>
                        {opp && (
                          <p className="font-medium text-sm mt-1">{opp.title}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Referred by: {ref.referred_by_name}
                        </p>
                        {ref.notes && (
                          <p className="text-xs text-muted-foreground mt-1 bg-muted/50 rounded-lg p-2">
                            {ref.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    {canUpdateStatus && ref.status !== 'completed' && (
                      <Select value={ref.status} onValueChange={(v) => handleStatusChange(ref.id, v)}>
                        <SelectTrigger className="w-32 rounded-full text-xs" data-testid={`referral-status-${ref.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-2 border-dashed rounded-2xl">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">
              {filter === 'all' ? 'No referrals yet.' : `No ${filter} referrals.`}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
